/**
 * Bestandskunde-Erkennung: Wahrheit = Excel-E-Mail + Kundennummer (z. B. 22/2022).
 * Kein PEO / MA-I.
 */

export const BESTAND_MANUAL_REVIEW_MSG =
    'E-Mail-Adresse oder Mandant-Nummer stimmt nicht mit unseren Bestandsdaten überein. Bitte Angaben prüfen — bei Abweichung manuell im Admin nachziehen.';

/** Extrahiert Kundennummer-Kern wie 22/2022 (laufende Nr./Jahr). */
export const normalizeCustomerNumberCore = (raw: string | null | undefined): string | null => {
    if (!raw || typeof raw !== 'string') return null;
    const s = raw.trim().replace(/\s+/g, ' ');
    // Bevorzugt NN/YYYY oder NNNN/YYYY (auch mit Punkt/Bindestrich)
    const slash = s.match(/(\d{1,4})[./-](\d{4})/);
    if (slash) {
        const n = String(parseInt(slash[1], 10));
        const y = slash[2];
        if (n && y && Number(y) >= 2000 && Number(y) < 2100) {
            return `${n}/${y}`;
        }
    }
    return null;
};

export const emailsMatch = (a: string | null | undefined, b: string | null | undefined): boolean => {
    const x = String(a || '').trim().toLowerCase();
    const y = String(b || '').trim().toLowerCase();
    return !!x && !!y && x === y;
};

export type BestandMatchResult = {
    matched: boolean;
    emailOk: boolean;
    numberOk: boolean;
    vorabId: string | null;
    monthlyFee: number | null;
    needsManualReview: boolean;
};

const readFee = (d: any): number | null => {
    const raw = d?.monthlyFee ?? d?.clientData?.monthlyFee;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
        const cleaned = raw.trim().replace(/\./g, '').replace(',', '.');
        const n = parseFloat(cleaned);
        return Number.isFinite(n) ? n : null;
    }
    return null;
};

const docCustomerCores = (d: any): string[] => {
    const candidates = [
        d?.customerNumber,
        d?.existingCustomerNumber,
        d?.providedOldId,
        d?.mandantNr,
        d?.clientData?.customerNumber,
        d?.clientData?.existingCustomerNumber,
        d?.clientData?.providedOldId,
        d?.clientData?.mandantNr,
    ];
    const cores: string[] = [];
    for (const c of candidates) {
        const core = normalizeCustomerNumberCore(String(c || ''));
        if (core && !cores.includes(core)) cores.push(core);
    }
    return cores;
};

const docEmails = (d: any): string[] => {
    const list = [
        d?.email,
        d?.mailadresse,
        d?.clientData?.mailadresse,
        d?.clientData?.email,
        d?.clientData?.contact?.email,
    ];
    return list
        .map((e) => String(e || '').trim().toLowerCase())
        .filter(Boolean);
};

/**
 * Sucht Vorab-/Bestandsakte (isLegacy oder gleiche Kd.-Nr./E-Mail) in einer Doc-Liste.
 */
export const matchBestandAgainstDocs = (
    email: string,
    customerNumberRaw: string,
    docs: Array<{ id: string; data: any }>
): BestandMatchResult => {
    const emailNorm = String(email || '').trim().toLowerCase();
    const core = normalizeCustomerNumberCore(customerNumberRaw);

    let emailHit: { id: string; data: any } | null = null;
    let numberHit: { id: string; data: any } | null = null;

    for (const row of docs) {
        const d = row.data || {};
        const emails = docEmails(d);
        const cores = docCustomerCores(d);
        if (emailNorm && emails.includes(emailNorm) && !emailHit) {
            emailHit = row;
        }
        if (core && cores.includes(core) && !numberHit) {
            numberHit = row;
        }
    }

    const emailOk = !!emailHit;
    const numberOk = !!numberHit;

    // Beide Treffer auf derselben Akte = sauber erkannt
    if (emailHit && numberHit && emailHit.id === numberHit.id) {
        return {
            matched: true,
            emailOk: true,
            numberOk: true,
            vorabId: emailHit.id,
            monthlyFee: readFee(emailHit.data),
            needsManualReview: false,
        };
    }

    // Beide ok, aber unterschiedliche Docs → manuell
    if (emailHit && numberHit && emailHit.id !== numberHit.id) {
        return {
            matched: false,
            emailOk: true,
            numberOk: true,
            vorabId: emailHit.id,
            monthlyFee: readFee(emailHit.data),
            needsManualReview: true,
        };
    }

    // E-Mail trifft, aber keine Kd.-Nr. angegeben → kein Widerspruch (etablierte
    // Bestandskunden oft nur mit Mandant-Nr. / Vertragsbeginn, ohne separates Vorab-Feld).
    if (emailHit && !core) {
        return {
            matched: true,
            emailOk: true,
            numberOk: false,
            vorabId: emailHit.id,
            monthlyFee: readFee(emailHit.data),
            needsManualReview: false,
        };
    }

    // Nur eines stimmt oder keines → Meldung manuell
    const prefer = emailHit || numberHit;
    return {
        matched: false,
        emailOk,
        numberOk,
        vorabId: prefer?.id || null,
        monthlyFee: prefer ? readFee(prefer.data) : null,
        needsManualReview: true,
    };
};
